import { z } from 'zod';
import { emailSchema, newPasswordSchema } from './auth.js';

/**
 * Platform team and security (ADR-0157, BR-261): operator invitations, roles and status from
 * /platform (super admin), the invitee's set-up steps, my sessions and team activity.
 */
export const TEAM_OPERATOR_ROLES = [
  'platform.super_admin',
  'platform.support',
  'platform.billing',
] as const;
export type TeamOperatorRole = (typeof TEAM_OPERATOR_ROLES)[number];
export const TEAM_OPERATOR_STATUSES = ['active', 'disabled'] as const;
export type TeamOperatorStatus = (typeof TEAM_OPERATOR_STATUSES)[number];

/** Days of audited actions counted on the team page. */
export const TEAM_ACTIVITY_DAYS = 30;
/** Lifetime of an operator invitation link, in hours. */
export const TEAM_INVITATION_TTL_HOURS = 72;

const roleSchema = z.enum(TEAM_OPERATOR_ROLES);
const totpCodeSchema = z.string().regex(/^\d{6}$/);
const tokenSchema = z.string().min(20).max(200);

// ---------------------------------------------------------------- team page (super admin)
export const teamActivityCountSchema = z.object({
  action: z.string(),
  count: z.number().int(),
});

export const teamOperatorSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  role: roleSchema,
  status: z.enum(TEAM_OPERATOR_STATUSES),
  lastLoginAt: z.string().nullable(),
  createdAt: z.string(),
  isMe: z.boolean(),
  /** Audited actions in the last TEAM_ACTIVITY_DAYS days, most frequent first. */
  activityTotal: z.number().int(),
  activity: z.array(teamActivityCountSchema),
});
export type TeamOperator = z.infer<typeof teamOperatorSchema>;

export const teamInvitationSchema = z.object({
  id: z.string(),
  email: z.string(),
  fullName: z.string(),
  role: roleSchema,
  invitedByName: z.string().nullable(),
  createdAt: z.string(),
  expiresAt: z.string(),
  /** The invitee has chosen a password and still has to confirm the authenticator. */
  started: z.boolean(),
});
export type TeamInvitation = z.infer<typeof teamInvitationSchema>;

export const teamOverviewSchema = z.object({
  activityDays: z.number().int(),
  operators: z.array(teamOperatorSchema),
  invitations: z.array(teamInvitationSchema),
});
export type TeamOverview = z.infer<typeof teamOverviewSchema>;

export const inviteOperatorSchema = z.object({
  email: emailSchema,
  fullName: z.string().trim().min(2).max(120),
  role: roleSchema,
});
export type InviteOperator = z.input<typeof inviteOperatorSchema>;

export const changeOperatorRoleSchema = z.object({ role: roleSchema });
export type ChangeOperatorRole = z.input<typeof changeOperatorRoleSchema>;

export const changeOperatorStatusSchema = z.object({ status: z.enum(TEAM_OPERATOR_STATUSES) });
export type ChangeOperatorStatus = z.input<typeof changeOperatorStatusSchema>;

// ---------------------------------------------------------------- invitee (public)
export const operatorInvitationTokenSchema = z.object({ token: tokenSchema });
export type OperatorInvitationToken = z.input<typeof operatorInvitationTokenSchema>;

export const operatorInvitationViewSchema = z.object({
  email: z.string(),
  fullName: z.string(),
  role: roleSchema,
  expiresAt: z.string(),
});
export type OperatorInvitationView = z.infer<typeof operatorInvitationViewSchema>;

export const operatorInvitationStartSchema = z.object({
  token: tokenSchema,
  password: newPasswordSchema,
});
export type OperatorInvitationStart = z.input<typeof operatorInvitationStartSchema>;

/** Shown once: the authenticator secret and its otpauth:// address (for the QR code). */
export const operatorInvitationSecretSchema = z.object({
  totpSecret: z.string(),
  otpauthUri: z.string(),
});
export type OperatorInvitationSecret = z.infer<typeof operatorInvitationSecretSchema>;

export const operatorInvitationCompleteSchema = z.object({
  token: tokenSchema,
  code: totpCodeSchema,
});
export type OperatorInvitationComplete = z.input<typeof operatorInvitationCompleteSchema>;

// ---------------------------------------------------------------- my sessions
export const operatorSessionSchema = z.object({
  id: z.string(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  createdAt: z.string(),
  lastSeenAt: z.string(),
  current: z.boolean(),
});
export type OperatorSession = z.infer<typeof operatorSessionSchema>;
export const operatorSessionListSchema = z.object({ items: z.array(operatorSessionSchema) });

export const endedSessionsSchema = z.object({ ended: z.number().int() });
